import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TOOL_LABELS, bookieLogos } from './CommunityBetTracker';
import AddTipToTrackerModal from './AddTipToTrackerModal';
import resultsTrackerService from '../../utils/services/resultsTrackerService';

/**
 * Public Tip Log — the read-only counterpart to the admin CommunityBetTracker
 * table. Fed by `GET /community/tips?type=active`, so it shows only active
 * (pending) tips, each enriched with live-odds fields:
 * `current_bookmaker_odds` and `odds_change_percentage`.
 *
 * Same row treatment as the admin table (tool chips, bookie logos) but with
 * zero management affordances: no add form, no edit/delete/reorder, no
 * quick-result dropdown (the result is a static chip), and no date column.
 * Results graded by admins via Admin Tools flow straight through — a resulted
 * tip simply drops out of the active list on the next load.
 */

// Resolve a friendly tipper label from a tip object
const getTipperLabel = (tip) =>
    tip.tipper_discord_name || tip.tipper_email || (tip.user_id != null ? `User #${tip.user_id}` : 'Unknown');

// Format a unit value accurately (up to 2 dp) and strip trailing zeros so e.g.
// 1 -> "1", 1.8 -> "1.8", 0.25 -> "0.25", 4.7 -> "4.7".
const cleanUnits = (n) => {
    const rounded = Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2).replace(/\.?0+$/, '');
};

// Chip-styled trigger that opens a popover menu — same pattern as the Prop
// Tool / market overview filter chips. Closes on outside click.
function ChipDropdown({ summary, active, panelWidth = 'w-56', className = '', children }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div className={`relative ${className}`} ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
                className={`w-full md:w-auto flex items-center justify-between md:justify-start gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
                    active
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                        : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-slate-200 hover:border-slate-600'
                }`}
            >
                <span className="truncate max-w-[200px]">{summary}</span>
                <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className={`absolute z-50 left-0 mt-1.5 ${panelWidth} max-h-64 overflow-y-auto styled-scrollbar rounded-xl border border-slate-700/70 bg-slate-900 shadow-2xl shadow-black/60`}>
                    {children({ close: () => setOpen(false) })}
                </div>
            )}
        </div>
    );
}

// A single row inside a ChipDropdown menu (tick marks the selection).
function ChipMenuItem({ label, checked, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] transition-colors hover:bg-slate-800/60"
        >
            <span className="w-3.5 flex-shrink-0">
                {checked && (
                    <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                )}
            </span>
            <span className={`flex-1 flex items-center gap-2 truncate font-mono ${checked ? 'text-white' : 'text-slate-300'}`}>{label}</span>
        </button>
    );
}

// Numeric columns that may be null (enrichment unavailable) — nulls always
// sort to the bottom regardless of direction.
const NULLABLE_NUMERIC_KEYS = new Set(['current_bookmaker_odds', 'odds_change_percentage']);

// Module-level UI-state cache kept alive for the SPA session, so leaving the
// page (e.g. deep-linking into the Prop Tool) and coming back restores the
// table's search/filter/sort instead of resetting to defaults.
const tipLogStateCache = {};

// Maps a stat word from a bet description to the Prop Tool's market keys.
const STAT_KEY_BY_WORD = {
    disposal: 'disposals', disposals: 'disposals',
    goal: 'goals', goals: 'goals',
    mark: 'marks', marks: 'marks',
    tackle: 'tackles', tackles: 'tackles',
    kick: 'kicks', kicks: 'kicks',
    handball: 'handballs', handballs: 'handballs',
    clearance: 'clearances', clearances: 'clearances',
    hitout: 'hit_outs', hitouts: 'hit_outs',
    'hit out': 'hit_outs', 'hit outs': 'hit_outs',
};

const statKeyFromText = (text) => {
    const words = String(text).toLowerCase().replace(/[^a-z\s]/g, ' ').trim().split(/\s+/);
    if (!words.length) return null;
    return STAT_KEY_BY_WORD[words.slice(0, 2).join(' ')] || STAT_KEY_BY_WORD[words[0]] || null;
};

// Best-effort parse of an AFL tip's free-text bet description into a Prop
// Tool deep link. Handles the common shapes:
//   "Nick Daicos 25+ Disposals"          → line 24.5
//   "Nick Daicos - To Record 3+ Goals"   → line 2.5
//   "Nick Daicos Over 24.5 Disposals"    → line 24.5
// Returns null for anything unrecognised (multis, team bets, other sports),
// in which case the Prop Tool button simply isn't rendered.
const parsePropBet = (tip) => {
    if ((tip.sport || '').toLowerCase() !== 'afl' || !tip.bet) return null;
    const text = String(tip.bet).trim();

    let m = text.match(/^(.+?)\s*[-–—]?\s*(?:to record\s+)?(\d+(?:\.\d+)?)\s*\+\s*(.+)$/i);
    if (m) {
        const statKey = statKeyFromText(m[3]);
        if (!statKey) return null;
        return { player: m[1].trim(), propType: statKey, line: parseFloat(m[2]) - 0.5 };
    }

    m = text.match(/^(.+?)\s*[-–—]?\s*(?:over|under|[ou])\s*(\d+(?:\.\d+)?)\s+(.+)$/i);
    if (m) {
        const statKey = statKeyFromText(m[3]);
        if (!statKey) return null;
        return { player: m[1].trim(), propType: statKey, line: parseFloat(m[2]) };
    }

    return null;
};


function PublicTipLog({ tips, tipperRecords = {}, displayMode = 'units', unitSize = 1, isLoading = false, error = null, onRetry, onToast }) {
    const isUnitMode = displayMode === 'units';
    const navigate = useNavigate();
    const location = useLocation();

    // Deep-link into the Prop Tool with the tip's player, market and line
    // pre-selected — same contract as the Snipe Hub tables.
    const openPropTool = useCallback((tip, parsed) => {
        navigate('/prop-tool', {
            state: {
                playerName: parsed.player,
                propType: parsed.propType,
                line: parsed.line,
                bookmaker: tip.bookie ? String(tip.bookie).toLowerCase() : null,
                from: location.pathname,
            },
        });
    }, [navigate, location.pathname]);

    // Default ordering groups tips by round then game so bets from the same
    // match sit together. Clicking a column header overrides it. Restored from
    // the session cache so a Prop Tool detour doesn't reset the table.
    const [sortConfig, setSortConfig] = useState(tipLogStateCache.sortConfig ?? { key: 'round_game', direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState(tipLogStateCache.searchTerm ?? '');
    const [gameFilter, setGameFilter] = useState(tipLogStateCache.gameFilter ?? 'all');
    // Mobile-only: whether the collapsible filter row is expanded.
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    // Badge on the mobile filter toggle so active filters stay discoverable
    // while collapsed.
    const activeFilterCount = [gameFilter !== 'all', searchTerm.trim() !== ''].filter(Boolean).length;

    // Persist table filters/sort for the SPA session.
    useEffect(() => {
        tipLogStateCache.sortConfig = sortConfig;
        tipLogStateCache.searchTerm = searchTerm;
        tipLogStateCache.gameFilter = gameFilter;
    }, [sortConfig, searchTerm, gameFilter]);

    // Add-to-tracker: the tip currently open in the modal, plus the user's
    // unit size (fetched from their Results Tracker settings for the stake
    // shortcut buttons — same behaviour as the Snipe Feed).
    const [trackerTip, setTrackerTip] = useState(null);
    const [isAddingBet, setIsAddingBet] = useState(false);
    const [trackerUnitSize, setTrackerUnitSize] = useState(20);

    useEffect(() => {
        const fetchSettings = async () => {
            const result = await resultsTrackerService.getSettings();
            if (result.success && result.data) {
                const { advancedUnitSettings, monthlyUnits, unitSize: defaultUnit } = result.data;
                if (advancedUnitSettings && monthlyUnits?.length > 0) {
                    const now = new Date();
                    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    const match = monthlyUnits
                        .filter(m => m.startDate?.substring(0, 7) <= currentMonth)
                        .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
                    setTrackerUnitSize(match?.unitSize || defaultUnit || 20);
                } else {
                    setTrackerUnitSize(defaultUnit || 20);
                }
            }
        };
        fetchSettings();
    }, []);

    const handleAddToTracker = useCallback(async (betPayload) => {
        setIsAddingBet(true);
        const result = await resultsTrackerService.addBet(betPayload);
        if (result.success) {
            onToast?.('Bet added to tracker successfully', 'success');
            setTrackerTip(null);
        } else if (result.isAuthError) {
            onToast?.('Session expired. Please log in again.', 'error');
        } else {
            onToast?.(result.message || 'Failed to add bet to tracker', 'error');
        }
        setIsAddingBet(false);
        return result;
    }, [onToast]);

    const matchesSearch = useCallback((t, term) => {
        return (
            (t.game || '').toLowerCase().includes(term) ||
            (t.bet || '').toLowerCase().includes(term) ||
            (t.bookie || '').toLowerCase().includes(term) ||
            getTipperLabel(t).toLowerCase().includes(term) ||
            (TOOL_LABELS[t.tipped_tool] || t.tipped_tool || '').toLowerCase().includes(term)
        );
    }, []);

    // Unique game labels present in the active tips, driving the game chip.
    const gameOptions = useMemo(() => {
        return [...new Set(tips.map(t => t.game).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    }, [tips]);

    // Reset the game filter if its game disappears from the active list
    // (e.g. after a background refresh resulted/removed those tips). Waits for
    // the fetch to settle so a session-restored filter isn't wiped while the
    // list is still empty on a return visit.
    useEffect(() => {
        if (isLoading || gameOptions.length === 0) return;
        if (gameFilter !== 'all' && !gameOptions.includes(gameFilter)) {
            setGameFilter('all');
        }
    }, [gameFilter, gameOptions, isLoading]);

    // Game filter applied first — search and the footer totals both respect it.
    const gameFilteredTips = useMemo(() => {
        if (gameFilter === 'all') return tips;
        return tips.filter(t => t.game === gameFilter);
    }, [tips, gameFilter]);

    const searchFilteredTips = useMemo(() => {
        if (!searchTerm) return gameFilteredTips;
        const term = searchTerm.toLowerCase();
        return gameFilteredTips.filter(t => matchesSearch(t, term));
    }, [gameFilteredTips, searchTerm, matchesSearch]);

    const processedTips = useMemo(() => {
        let filtered = [...gameFilteredTips];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(t => matchesSearch(t, term));
        }

        filtered.sort((a, b) => {
            if (sortConfig.key === 'round_game') {
                // Latest round first; tips without a round sort last.
                const aRound = a.round != null && String(a.round).trim() !== '' ? Number(String(a.round).replace(/[^0-9]/g, '')) : -Infinity;
                const bRound = b.round != null && String(b.round).trim() !== '' ? Number(String(b.round).replace(/[^0-9]/g, '')) : -Infinity;
                if (aRound !== bRound) return bRound - aRound;
                const gameCmp = (a.game || '').localeCompare(b.game || '');
                if (gameCmp !== 0) return gameCmp;
                return (a.sort_order ?? a.id) - (b.sort_order ?? b.id);
            }

            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortConfig.key === 'tipper') {
                aVal = getTipperLabel(a).toLowerCase();
                bVal = getTipperLabel(b).toLowerCase();
            } else if (NULLABLE_NUMERIC_KEYS.has(sortConfig.key)) {
                const missing = sortConfig.direction === 'asc' ? Infinity : -Infinity;
                aVal = aVal ?? missing;
                bVal = bVal ?? missing;
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;

            const aSortOrder = a.sort_order ?? a.id;
            const bSortOrder = b.sort_order ?? b.id;
            return sortConfig.direction === 'asc' ? aSortOrder - bSortOrder : bSortOrder - aSortOrder;
        });

        return filtered;
    }, [gameFilteredTips, sortConfig, searchTerm, matchesSearch]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    }, []);

    // Formatting helpers
    const formatStaked = (value) => {
        if (value === undefined || value === null) return '-';
        if (isUnitMode) {
            return `${cleanUnits(value / unitSize)}U`;
        }
        return `$${value.toFixed(2)}`;
    };

    const formatTotalStaked = (list) => {
        const total = list.reduce((sum, t) => sum + t.stake, 0);
        return isUnitMode ? `${cleanUnits(total / unitSize)}U` : `$${total.toFixed(2)}`;
    };

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
            return (
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            );
        }
        return sortConfig.direction === 'desc' ? (
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
        ) : (
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
        );
    };

    const columns = [
        { key: 'tipper', label: 'Tipper', width: 'min-w-[120px]' },
        { key: 'record', label: 'Record', width: 'w-[150px]', noSort: true },
        { key: 'tipped_tool', label: 'Tool', width: 'w-[120px]' },
        { key: 'game', label: 'Game', width: 'min-w-[140px]' },
        { key: 'bet', label: 'Bet', width: 'min-w-[220px]' },
        { key: 'current_bookmaker_odds', label: 'Tipped Price > Current Price', width: 'w-[170px]' },
        { key: 'odds_change_percentage', label: 'Odds Change (%)', width: 'w-[130px]' },
        { key: 'stake', label: 'Stake', width: 'w-[80px]' },
        { key: 'payout', label: 'Payout', width: 'w-[80px]' },
        { key: 'bookie', label: 'Bookie', width: 'w-[80px]' },
        { key: 'track', label: '', width: 'w-[60px]', noSort: true }
    ];

    return (
        <div className="flex-1 flex flex-col bg-slate-900/50 rounded-2xl border border-slate-700/50 overflow-hidden">
            {/* Header */}
            <div className="p-4 md:p-6 border-b border-slate-700/50">
                <div className="flex flex-col md:flex-row items-stretch md:items-center md:justify-between gap-3">
                    <div className="flex items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2.5">
                            <h3 className="text-lg font-semibold text-white">Community Tips Feed</h3>
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                </span>
                                <span className="font-semibold">{tips.length}</span> active
                            </span>
                        </div>

                        {/* Expandable filter toggle (mobile only) */}
                        <button
                            type="button"
                            onClick={() => setMobileFiltersOpen((v) => !v)}
                            aria-expanded={mobileFiltersOpen}
                            aria-label="Toggle filters"
                            className={`md:hidden flex items-center justify-center px-2.5 py-1.5 rounded-lg border transition-colors ${
                                mobileFiltersOpen
                                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                    : 'border-slate-700/60 bg-slate-900/50 text-slate-300'
                            }`}
                        >
                            <span className="relative inline-flex">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M6 12h12M10 17h4" />
                                </svg>
                                {activeFilterCount > 0 && (
                                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 px-1 rounded-full bg-emerald-500 text-emerald-950 text-[8px] font-mono font-bold leading-none inline-flex items-center justify-center tabular-nums">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </span>
                        </button>
                    </div>

                    {/* Filters — always inline on desktop, collapsed behind the
                        toggle on mobile. */}
                    <div className={`${mobileFiltersOpen ? 'flex' : 'hidden'} md:flex items-center gap-2 md:gap-3`}>
                        <ChipDropdown
                            summary={gameFilter === 'all' ? 'All Games' : gameFilter}
                            active={gameFilter !== 'all'}
                            panelWidth="w-64"
                            className="flex-1 min-w-0 md:flex-none"
                        >
                            {({ close }) => (
                                <div className="py-1">
                                    <ChipMenuItem
                                        label="All Games"
                                        checked={gameFilter === 'all'}
                                        onClick={() => { setGameFilter('all'); close(); }}
                                    />
                                    {gameOptions.map(game => (
                                        <ChipMenuItem
                                            key={game}
                                            label={game}
                                            checked={gameFilter === game}
                                            onClick={() => { setGameFilter(game); close(); }}
                                        />
                                    ))}
                                </div>
                            )}
                        </ChipDropdown>

                        <div className="relative flex-1 min-w-0 md:flex-none">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search tips, tippers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full md:w-52"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-800/50">
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    onClick={() => !col.noSort && handleSort(col.key)}
                                    className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider transition-colors ${
                                        col.key === 'bookie' ? 'text-center' : 'text-left'
                                    } ${col.width || ''} ${!col.noSort ? 'cursor-pointer hover:text-white' : ''}`}
                                >
                                    <div className={`flex items-center gap-1 ${col.key === 'bookie' ? 'justify-center' : ''}`}>
                                        {col.label}
                                        {!col.noSort && <SortIcon column={col.key} />}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                        {isLoading ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-10">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="flex gap-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: '0ms' }} />
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: '200ms' }} />
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: '400ms' }} />
                                        </div>
                                        <span className="text-xs font-mono text-slate-500">Loading active tips</span>
                                    </div>
                                </td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-10">
                                    <div className="flex flex-col items-center gap-3 text-center">
                                        <span className="text-sm text-slate-400">{error.message || 'Failed to load active tips.'}</span>
                                        {onRetry && (
                                            <button
                                                onClick={onRetry}
                                                className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded text-xs font-mono font-medium hover:bg-emerald-500/15 transition-colors"
                                            >
                                                Try Again
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ) : processedTips.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">
                                    No active tips right now.
                                </td>
                            </tr>
                        ) : (
                            processedTips.map((tip) => (
                                <tr key={tip.id} className="transition-all hover:bg-slate-800/30">
                                    <td className="px-4 py-3 text-sm">
                                        <div className="flex items-center gap-2 max-w-[160px]">
                                            <span className="w-6 h-6 flex-shrink-0 rounded-full bg-slate-700/60 text-slate-300 text-[10px] font-bold flex items-center justify-center uppercase">
                                                {getTipperLabel(tip).charAt(0)}
                                            </span>
                                            <span className="text-slate-200 font-medium truncate" title={getTipperLabel(tip)}>{getTipperLabel(tip)}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {(() => {
                                            const rec = tipperRecords[getTipperLabel(tip).toLowerCase()];
                                            if (!rec || (rec.wins === 0 && rec.losses === 0)) {
                                                return <span className="text-sm text-slate-600">-</span>;
                                            }
                                            const units = rec.pl / unitSize;
                                            const settled = rec.wins + rec.losses;
                                            const winRate = settled > 0 ? Math.round((rec.wins / settled) * 100) : 0;
                                            const roi = rec.staked > 0 ? (rec.pl / rec.staked) * 100 : null;
                                            const plPositive = rec.pl >= 0;
                                            const chipTone = (positive) => positive
                                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25'
                                                : 'bg-red-500/10 text-red-400 border-red-500/25';
                                            return (
                                                <div
                                                    className="flex flex-col gap-1"
                                                    title={`${getTipperLabel(tip)} all-time: ${rec.wins}W-${rec.losses}L · ${winRate}% win rate${roi != null ? ` · ${roi >= 0 ? '+' : ''}${roi.toFixed(1)}% ROI` : ''}`}
                                                >
                                                    <div className="flex items-baseline justify-center gap-1.5">
                                                        <span className="text-sm font-mono font-semibold text-slate-200">{rec.wins}W-{rec.losses}L</span>
                                                        <span className="text-[10px] font-mono text-slate-500">{winRate}% WR</span>
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        <span className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-mono font-semibold border ${chipTone(plPositive)}`}>
                                                            {plPositive ? '+' : '-'}{cleanUnits(Math.abs(units))}U
                                                        </span>
                                                        {roi != null && (
                                                            <span className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-mono font-semibold border ${chipTone(roi >= 0)}`}>
                                                                {roi >= 0 ? '+' : ''}{roi.toFixed(1)}% ROI
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-700/40 text-slate-300 border border-slate-600/40 whitespace-nowrap">
                                            {TOOL_LABELS[tip.tipped_tool] || tip.tipped_tool || '—'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-white font-medium">
                                        <div className="max-w-[160px] flex items-center gap-1.5">
                                            <span className="truncate" title={tip.game}>{tip.game}</span>
                                            {tip.sport === 'afl' && tip.round != null && String(tip.round).trim() !== '' && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-700/40 text-slate-400 border border-slate-600/30 whitespace-nowrap flex-shrink-0">R{String(tip.round).replace(/[^0-9]/g, '')}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-300 align-middle">
                                        <div className="min-w-[200px] max-w-[340px] flex items-center gap-1.5">
                                            <span className="whitespace-normal break-words leading-snug" title={tip.bet}>{tip.bet}</span>
                                            {(() => {
                                                const parsed = parsePropBet(tip);
                                                if (!parsed) return null;
                                                return (
                                                    <button
                                                        type="button"
                                                        onClick={() => openPropTool(tip, parsed)}
                                                        title={`Open Prop Tool - ${parsed.player}`}
                                                        aria-label={`Open Prop Tool - ${parsed.player}`}
                                                        className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-md border border-sky-500/30 text-sky-400 hover:bg-sky-500/10 hover:border-sky-500/50 hover:text-sky-300 transition-all"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-sm whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            {bookieLogos[tip.bookie] && (
                                                <img src={bookieLogos[tip.bookie]} alt={tip.bookie} className="w-5 h-5 rounded object-cover flex-shrink-0" title={tip.bookie} />
                                            )}
                                            <span
                                                className={tip.current_bookmaker_odds != null ? 'text-slate-500' : 'text-slate-300'}
                                                title="Tipped odds"
                                            >
                                                {Number(tip.odds).toFixed(2)}
                                            </span>
                                            <svg className="w-3 h-3 flex-shrink-0 text-slate-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                            </svg>
                                            {tip.current_bookmaker_odds != null ? (
                                                <span
                                                    className={`font-semibold ${
                                                        tip.current_bookmaker_odds >= tip.odds ? 'text-emerald-400' : 'text-red-400'
                                                    }`}
                                                    title="Current odds"
                                                >
                                                    {Number(tip.current_bookmaker_odds).toFixed(2)}
                                                </span>
                                            ) : (
                                                <span className="text-slate-600" title="No live price available">-</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {tip.odds_change_percentage != null ? (
                                            <span
                                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium font-mono tabular-nums border whitespace-nowrap ${
                                                    tip.odds_change_percentage > 0
                                                        ? 'bg-red-500/15 text-red-400 border-red-500/30'
                                                        : tip.odds_change_percentage < 0
                                                            ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                                            : 'bg-slate-700/40 text-slate-400 border-slate-600/40'
                                                }`}
                                                title={tip.odds_change_percentage > 0 ? 'Price has shortened since the tip — value may be gone' : tip.odds_change_percentage < 0 ? 'Price has drifted since the tip — better odds available than tipped' : 'No movement since the tip'}
                                            >
                                                {tip.odds_change_percentage !== 0 && (
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d={tip.odds_change_percentage > 0 ? 'M12 19V5m0 0l-6 6m6-6l6 6' : 'M12 5v14m0 0l6-6m-6 6l-6-6'} />
                                                    </svg>
                                                )}
                                                {tip.odds_change_percentage > 0 ? '+' : ''}{Number(tip.odds_change_percentage).toFixed(2)}%
                                            </span>
                                        ) : (
                                            <span className="text-sm text-slate-600" title="No live price available">-</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-300">{formatStaked(tip.stake)}</td>
                                    <td className="px-4 py-3 text-sm text-slate-300">{tip.payout > 0 ? formatStaked(tip.payout) : '-'}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-center">
                                            {bookieLogos[tip.bookie] ? (
                                                <img src={bookieLogos[tip.bookie]} alt={tip.bookie} className="w-6 h-6 rounded object-cover" title={tip.bookie} />
                                            ) : (
                                                <span className="text-sm text-slate-300">{tip.bookie}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            type="button"
                                            onClick={() => setTrackerTip(tip)}
                                            title="Add to your results tracker"
                                            className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Summary Footer */}
            {processedTips.length > 0 && (
                <div className="p-4 bg-slate-800/30 border-t border-slate-700/50">
                    <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                        <div className="text-slate-400">
                            Showing <span className="text-white font-medium">{processedTips.length}</span> of <span className="text-white font-medium">{tips.length}</span> active tips
                        </div>
                        <div className="text-slate-400">
                            Total Staked: <span className="text-white font-medium">{formatTotalStaked(searchFilteredTips)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Add to Tracker Modal */}
            {trackerTip && (
                <AddTipToTrackerModal
                    tip={trackerTip}
                    unitSize={trackerUnitSize}
                    onAddToTracker={handleAddToTracker}
                    isAddingBet={isAddingBet}
                    onClose={() => setTrackerTip(null)}
                />
            )}
        </div>
    );
}

export default PublicTipLog;
